import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  Pencil,
  X,
  Save,
  LogIn,
  ShieldCheck,
  ArrowLeft,
  ExternalLink,
} from "lucide-react";
import { API_URL } from "../utils/api";

/**
 * Admin — TaxSetu AI Blog Editor
 * =============================
 * Password-gated page for the TaxSetu team to author blog posts alongside the
 * auto-generated Regulatory Pulse feed. All posts live in the same
 * `regulatory_pulse_posts` collection and appear on the landing page + ticker.
 *
 * Auth: password stored in sessionStorage; sent via X-Admin-Password header.
 */

const CATEGORIES = [
  "Income Tax",
  "GST",
  "Labour Law",
  "Companies Act",
  "FEMA/RBI",
  "Capital Markets",
  "Case Laws",
  "Stock Markets",
  "Mixed",
];

const STATUSES = [
  "Reported",
  "Proposed",
  "Recommended",
  "Cabinet-Approved",
  "Notified",
  "Effective",
];

const empty = {
  title: "",
  category: "Income Tax",
  status: "Reported",
  notification_ref: "",
  what_changed: "",
  impact: "",
  recommendation: "",
  teaser: "",
};

export default function AdminBlogPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState(
    () => (typeof window !== "undefined" && sessionStorage.getItem("tsai_admin_pw")) || ""
  );
  const [authed, setAuthed] = useState(false);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingSlug, setEditingSlug] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const headers = { "X-Admin-Password": password };

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/pulse/admin/list`, { headers });
      if (res.status === 403) {
        setAuthed(false);
        sessionStorage.removeItem("tsai_admin_pw");
        toast.error("Invalid password");
        return;
      }
      if (!res.ok) throw new Error("Failed to load");
      const data = await res.json();
      setPosts(data.posts || []);
      setAuthed(true);
      sessionStorage.setItem("tsai_admin_pw", password);
    } catch (e) {
      toast.error("Could not fetch posts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (password && !authed) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew = () => {
    setForm(empty);
    setEditingSlug(null);
    setShowForm(true);
  };

  const openEdit = (post) => {
    setForm({
      title: post.title || "",
      category: post.category || "Mixed",
      status: post.status || "Reported",
      notification_ref: post.notification_ref || "",
      what_changed: post.what_changed || "",
      impact: post.impact || "",
      recommendation: post.recommendation || "",
      teaser: post.teaser || "",
    });
    setEditingSlug(post.slug);
    setShowForm(true);
  };

  const submit = async () => {
    // basic client-side validation
    for (const k of ["title", "what_changed", "impact", "recommendation"]) {
      if (!form[k] || form[k].trim().length < 10) {
        toast.error(`Please fill in "${k.replace("_", " ")}" (min 10 chars)`);
        return;
      }
    }
    setSaving(true);
    try {
      const url = editingSlug
        ? `${API_URL}/pulse/admin/${editingSlug}`
        : `${API_URL}/pulse/admin/create`;
      const method = editingSlug ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Save failed");
      }
      toast.success(editingSlug ? "Post updated" : "Post published");
      setShowForm(false);
      setForm(empty);
      setEditingSlug(null);
      load();
    } catch (e) {
      toast.error(String(e.message || e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (slug) => {
    if (!window.confirm("Delete this blog post permanently?")) return;
    try {
      const res = await fetch(`${API_URL}/pulse/admin/${slug}`, {
        method: "DELETE",
        headers,
      });
      if (!res.ok) throw new Error("Delete failed");
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(String(e.message || e));
    }
  };

  // ── Login screen ───────────────────────────────────────────────────────────
  if (!authed) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-6">
        <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900/60 p-8">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className="w-5 h-5 text-cyan-300" />
            <h1 className="text-lg font-bold">TaxSetu Blog Admin</h1>
          </div>
          <p className="text-[13px] text-slate-400 mb-5">
            Enter the admin password to create and manage blog posts.
          </p>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Admin password"
            className="mb-3 bg-slate-950 border-slate-700 text-white"
            data-testid="admin-password-input"
            onKeyDown={(e) => e.key === "Enter" && load()}
          />
          <Button
            onClick={load}
            disabled={!password || loading}
            className="w-full bg-cyan-500 hover:bg-cyan-400 text-slate-950"
            data-testid="admin-login-btn"
          >
            <LogIn className="w-4 h-4 mr-2" />
            {loading ? "Signing in…" : "Sign in"}
          </Button>
          <button
            onClick={() => navigate("/")}
            className="mt-4 w-full text-[12px] text-slate-500 hover:text-slate-300 flex items-center justify-center gap-1"
          >
            <ArrowLeft className="w-3 h-3" />
            Back to TaxSetu AI
          </button>
        </div>
      </div>
    );
  }

  // ── Main admin dashboard ───────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <button
              onClick={() => navigate("/")}
              className="text-[12px] text-slate-500 hover:text-slate-300 flex items-center gap-1 mb-2"
            >
              <ArrowLeft className="w-3 h-3" />
              Back to site
            </button>
            <h1 className="text-2xl sm:text-3xl font-bold text-white leading-tight">
              TaxSetu AI Blog · Admin
            </h1>
            <p className="text-[13px] text-slate-400 mt-1">
              Write and manage blog posts that appear on the landing page & ticker.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => {
                sessionStorage.removeItem("tsai_admin_pw");
                setPassword("");
                setAuthed(false);
              }}
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
              data-testid="admin-logout-btn"
            >
              Log out
            </Button>
            <Button
              onClick={openNew}
              className="bg-cyan-500 hover:bg-cyan-400 text-slate-950"
              data-testid="admin-new-post-btn"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              New blog
            </Button>
          </div>
        </div>

        {/* Post list */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-24 rounded-xl bg-slate-800/40 animate-pulse" />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-8 text-center text-slate-500">
            No posts yet. Tap “New blog” to publish the first one.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {posts.map((p) => (
              <div
                key={p.slug}
                className="group rounded-xl border border-slate-800 bg-slate-900/40 p-4 hover:border-slate-700 transition-colors"
                data-testid={`admin-post-${p.slug}`}
              >
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="text-[9.5px] font-bold uppercase tracking-widest text-cyan-300 border border-cyan-500/30 rounded-full px-2 py-0.5">
                    {p.category}
                  </span>
                  {p.status && (
                    <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-md border border-white/15 text-slate-300 bg-white/[0.04]">
                      {p.status}
                    </span>
                  )}
                  {p.author && (
                    <span className="text-[9px] font-semibold uppercase tracking-wider text-emerald-300">
                      · {p.author}
                    </span>
                  )}
                  <span className="ml-auto text-[10px] text-slate-500 font-mono uppercase">
                    {p.published_at
                      ? new Date(p.published_at).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                        })
                      : ""}
                  </span>
                </div>
                <p className="text-[14px] font-semibold text-white leading-snug mb-3 line-clamp-2">
                  {p.title}
                </p>
                <div className="flex items-center gap-3 text-[12px]">
                  <button
                    onClick={() => openEdit(p)}
                    className="text-cyan-300 hover:text-cyan-200 flex items-center gap-1"
                    data-testid={`admin-edit-${p.slug}`}
                  >
                    <Pencil className="w-3 h-3" />
                    Edit
                  </button>
                  <button
                    onClick={() => remove(p.slug)}
                    className="text-rose-300 hover:text-rose-200 flex items-center gap-1"
                    data-testid={`admin-delete-${p.slug}`}
                  >
                    <Trash2 className="w-3 h-3" />
                    Delete
                  </button>
                  <a
                    href={`/pulse/${p.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-auto text-slate-400 hover:text-slate-200 flex items-center gap-1"
                  >
                    <ExternalLink className="w-3 h-3" />
                    View
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Slide-over form */}
      {showForm && (
        <div
          className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-md flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto"
          onClick={() => setShowForm(false)}
        >
          <div
            className="w-full max-w-2xl rounded-2xl border border-slate-800 bg-slate-950 shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
              <h2 className="text-lg font-bold text-white">
                {editingSlug ? "Edit blog post" : "New blog post"}
              </h2>
              <button
                onClick={() => setShowForm(false)}
                className="text-slate-400 hover:text-white p-1 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 block mb-1.5">
                  Title (auto-prefixed with "TaxSetu Insight:" if you skip it)
                </label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Cabinet approves EPFO wage ceiling hike…"
                  className="bg-slate-900 border-slate-700 text-white"
                  data-testid="admin-form-title"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 block mb-1.5">
                    Category
                  </label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full h-10 rounded-md bg-slate-900 border border-slate-700 text-white px-3 text-sm"
                    data-testid="admin-form-category"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 block mb-1.5">
                    Status
                  </label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="w-full h-10 rounded-md bg-slate-900 border border-slate-700 text-white px-3 text-sm"
                    data-testid="admin-form-status"
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 block mb-1.5">
                  Official reference (optional)
                </label>
                <Input
                  value={form.notification_ref}
                  onChange={(e) => setForm({ ...form, notification_ref: e.target.value })}
                  placeholder="CBDT Notification 45/2026 or PIB press release dt. 15 Sep 2026"
                  className="bg-slate-900 border-slate-700 text-white"
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 block mb-1.5">
                  What changed *
                </label>
                <textarea
                  value={form.what_changed}
                  onChange={(e) => setForm({ ...form, what_changed: e.target.value })}
                  rows={4}
                  placeholder="Explain the notification / law / limit change…"
                  className="w-full rounded-md bg-slate-900 border border-slate-700 text-white p-3 text-sm resize-y"
                  data-testid="admin-form-what"
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 block mb-1.5">
                  Impact on taxpayers & practitioners *
                </label>
                <textarea
                  value={form.impact}
                  onChange={(e) => setForm({ ...form, impact: e.target.value })}
                  rows={3}
                  placeholder="Practical takeaway for CAs, businesses, taxpayers…"
                  className="w-full rounded-md bg-slate-900 border border-slate-700 text-white p-3 text-sm resize-y"
                  data-testid="admin-form-impact"
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 block mb-1.5">
                  TaxSetu AI recommendation *
                </label>
                <textarea
                  value={form.recommendation}
                  onChange={(e) => setForm({ ...form, recommendation: e.target.value })}
                  rows={3}
                  placeholder="Actionable compliance advice, timelines, filings…"
                  className="w-full rounded-md bg-slate-900 border border-slate-700 text-white p-3 text-sm resize-y"
                  data-testid="admin-form-rec"
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 block mb-1.5">
                  Teaser (short summary for ticker; leave blank to auto-derive)
                </label>
                <Input
                  value={form.teaser}
                  onChange={(e) => setForm({ ...form, teaser: e.target.value })}
                  placeholder="One-line summary shown on cards & ticker"
                  className="bg-slate-900 border-slate-700 text-white"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-800">
              <Button
                variant="outline"
                onClick={() => setShowForm(false)}
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                Cancel
              </Button>
              <Button
                onClick={submit}
                disabled={saving}
                className="bg-cyan-500 hover:bg-cyan-400 text-slate-950"
                data-testid="admin-form-submit"
              >
                <Save className="w-4 h-4 mr-1.5" />
                {saving ? "Saving…" : editingSlug ? "Update post" : "Publish post"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
